import { useEffect, useId, useRef, useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { COLOR_GROUPS, GROUP_INFO, NAME_MAX_LENGTH, ROOM_CODE_LENGTH } from "@grundbesitz/shared";
import { AppHeader } from "../components/AppHeader";
import { CodeTiles } from "../components/lobby/CodeTiles";
import { Button } from "../components/ui/Button";
import { navigate } from "../lib/router";
import { createRoom, roomInfo, saveName, savedName, useStore } from "../lib/store";
import { cleanName, isCompleteCode, normalizeCode } from "./homeLogic";

interface HomeProps {
  /** Why joining the room from the URL failed (unknown room, room full ...). */
  roomError?: string;
  /** Room code from the URL that the player has to join or that failed. */
  pendingCode?: string;
  /** Room code from the URL while the connection is being set up. */
  connectingTo?: string;
}

const inputClass =
  "h-12 w-full rounded-lg border border-line bg-surface px-3 text-base text-ink placeholder:text-ink-2 aria-[invalid=true]:border-danger";

export function Home({ roomError, pendingCode, connectingTo }: HomeProps) {
  let body: ReactNode;
  if (connectingTo) body = <Connecting code={connectingTo} />;
  else if (pendingCode && roomError) body = <JoinFailed code={pendingCode} message={roomError} />;
  else if (pendingCode) body = <NamePrompt code={pendingCode} />;
  else body = <StartForms />;

  return (
    <div className="flex min-h-dvh flex-col">
      <AppHeader />
      <main className="mx-auto w-full max-w-6xl flex-1 px-4 py-8 sm:px-6 sm:py-14">{body}</main>
    </div>
  );
}

// ---------------------------------------------------------------- start page

function StartForms() {
  const nameId = useId();
  const codeId = useId();
  const nameRef = useRef<HTMLInputElement>(null);
  const [name, setName] = useState(() => savedName().slice(0, NAME_MAX_LENGTH));
  const [code, setCode] = useState("");
  const [nameError, setNameError] = useState<string | null>(null);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState<"create" | "join" | null>(null);

  useEffect(() => {
    if (!name) nameRef.current?.focus();
  }, []); // only on first render

  const requireName = (): string | null => {
    const clean = cleanName(name);
    if (!clean) {
      setNameError("Gib zuerst deinen Namen ein.");
      nameRef.current?.focus();
      return null;
    }
    setNameError(null);
    return clean;
  };

  const create = async (e: FormEvent) => {
    e.preventDefault();
    setFormError(null);
    const clean = requireName();
    if (!clean || busy) return;
    setBusy("create");
    try {
      const { code: created } = await createRoom(clean);
      saveName(clean);
      navigate(`/${created}`);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "Das Spiel konnte nicht erstellt werden.");
    } finally {
      setBusy(null);
    }
  };

  const join = async (e: FormEvent) => {
    e.preventDefault();
    setFormError(null);
    const clean = requireName();
    if (!clean || busy) return;
    if (!isCompleteCode(code)) {
      setCodeError(`Der Code hat ${ROOM_CODE_LENGTH} Zeichen, zum Beispiel K7Q2ZP.`);
      document.getElementById(codeId)?.focus();
      return;
    }
    setCodeError(null);
    setBusy("join");
    try {
      const info = await roomInfo(code);
      if (!info.exists) {
        setCodeError("Diesen Raum gibt es nicht. Prüfe den Code.");
        document.getElementById(codeId)?.focus();
        return;
      }
      saveName(clean);
      navigate(`/${code}`);
    } catch {
      setFormError("Der Server ist nicht erreichbar. Versuche es gleich noch einmal.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="grid items-start gap-10 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,26rem)] lg:gap-16">
      <section aria-labelledby="intro-title" className="lg:pt-4">
        <GroupBand />
        <h1
          id="intro-title"
          className="mt-6 max-w-[16ch] font-display text-[2.6rem] font-bold leading-[1.02] tracking-[-0.03em] text-ink sm:text-6xl lg:text-7xl"
        >
          Das Brettspiel um Straßen, Mieten und Bahnhöfe.
        </h1>
        <p className="mt-6 max-w-[34rem] text-lg leading-relaxed text-ink">
          Ihr würfelt, kauft freie Grundstücke, baut Häuser und Hotels und kassiert Miete. Wer als Letzter zahlungsfähig
          bleibt, gewinnt. Gespielt wird mit 2 bis 8 Personen im Browser, ohne Konto.
        </p>
        <p className="mt-4 max-w-[34rem] border-l-4 border-accent pl-4 text-ink-2">
          Läuft ein Spiel schon, kommst du als Zuschauer hinein und siehst live mit.
        </p>
      </section>

      <div className="rounded-2xl border border-line bg-surface p-5 sm:p-7">
        <form onSubmit={create} noValidate>
          <label htmlFor={nameId} className="mb-1.5 block text-sm font-semibold text-ink">
            Dein Name
          </label>
          <input
            ref={nameRef}
            id={nameId}
            value={name}
            maxLength={NAME_MAX_LENGTH}
            autoComplete="nickname"
            enterKeyHint="go"
            aria-invalid={nameError !== null}
            aria-describedby={nameError ? `${nameId}-error` : undefined}
            onChange={(e) => {
              setName(e.target.value);
              if (nameError) setNameError(null);
            }}
            className={inputClass}
          />
          {nameError && (
            <p id={`${nameId}-error`} role="alert" className="mt-1.5 text-sm text-danger">
              {nameError}
            </p>
          )}
          <Button type="submit" variant="primary" className="mt-4 w-full min-h-12 text-base" disabled={busy !== null}>
            {busy === "create" ? "Erstelle Spiel ..." : "Spiel erstellen"}
          </Button>
        </form>

        <div className="my-6 flex items-center gap-3 text-sm text-ink-2" aria-hidden="true">
          <span className="h-px flex-1 bg-line" />
          oder
          <span className="h-px flex-1 bg-line" />
        </div>

        <form onSubmit={join} noValidate>
          <label htmlFor={codeId} className="mb-1.5 block text-sm font-semibold text-ink">
            Raum-Code
          </label>
          <div className="flex gap-2">
            <input
              id={codeId}
              value={code}
              inputMode="text"
              autoCapitalize="characters"
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              enterKeyHint="go"
              placeholder="K7Q2ZP"
              aria-invalid={codeError !== null}
              aria-describedby={codeError ? `${codeId}-error` : `${codeId}-hint`}
              onChange={(e) => {
                setCode(normalizeCode(e.target.value));
                if (codeError) setCodeError(null);
              }}
              className={`${inputClass} min-w-0 flex-1 font-mono text-lg font-semibold uppercase tracking-[0.25em]`}
            />
            <Button type="submit" className="min-h-12 shrink-0 px-5" disabled={busy !== null}>
              {busy === "join" ? "Prüfe ..." : "Beitreten"}
            </Button>
          </div>
          {codeError ? (
            <p id={`${codeId}-error`} role="alert" className="mt-1.5 text-sm text-danger">
              {codeError}
            </p>
          ) : (
            <p id={`${codeId}-hint`} className="mt-1.5 text-sm text-ink-2">
              Den Code oder Einladungslink bekommst du vom Host.
            </p>
          )}
        </form>

        {formError && (
          <p role="alert" className="mt-4 rounded-lg border border-danger px-3 py-2 text-sm text-danger">
            {formError}
          </p>
        )}
      </div>
    </div>
  );
}

/** The eight color bands of the street groups, a nod to the board. Decorative only. */
function GroupBand() {
  return (
    <div className="flex h-3 max-w-[34rem] gap-1" aria-hidden="true">
      {COLOR_GROUPS.map((g) => (
        <span key={g} className="flex-1 first:rounded-l-full last:rounded-r-full" style={{ background: GROUP_INFO[g].color }} />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------- invite link states

function Centered({ children, label }: { children: ReactNode; label: string }) {
  return (
    <section aria-label={label} className="mx-auto flex max-w-xl flex-col items-start gap-6 py-6 sm:py-16">
      {children}
    </section>
  );
}

function Connecting({ code }: { code: string }) {
  return (
    <Centered label="Verbindung">
      <CodeTiles code={code} />
      <p role="status" className="font-display text-2xl font-semibold text-ink">
        Verbinde mit Raum {code} ...
      </p>
      <Button variant="ghost" onClick={() => navigate("/")}>
        Abbrechen
      </Button>
    </Centered>
  );
}

function JoinFailed({ code, message }: { code: string; message: string }) {
  const join = useStore((s) => s.join);
  const [busy, setBusy] = useState(false);
  const retry = async () => {
    setBusy(true);
    await join(code, savedName() || "Gast");
    setBusy(false);
  };
  return (
    <Centered label="Beitritt nicht möglich">
      <CodeTiles code={code} />
      <div role="alert">
        <h1 className="font-display text-2xl font-semibold text-ink">Beitritt zu Raum {code} nicht möglich</h1>
        <p className="mt-2 text-ink">{message}</p>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button variant="primary" onClick={() => navigate("/")}>
          Zur Startseite
        </Button>
        <Button onClick={() => void retry()} disabled={busy}>
          {busy ? "Versuche es ..." : "Erneut versuchen"}
        </Button>
      </div>
    </Centered>
  );
}

/** Invite link opened without a saved name: ask for it, then join. */
function NamePrompt({ code }: { code: string }) {
  const join = useStore((s) => s.join);
  const nameId = useId();
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const clean = cleanName(name);
    if (!clean) {
      setError("Gib zuerst deinen Namen ein.");
      return;
    }
    setBusy(true);
    saveName(clean);
    await join(code, clean);
    setBusy(false);
  };

  return (
    <Centered label="Einladung">
      <CodeTiles code={code} />
      <form onSubmit={submit} noValidate className="w-full max-w-sm">
        <h1 className="font-display text-2xl font-semibold text-ink">Du bist zu Raum {code} eingeladen</h1>
        <label htmlFor={nameId} className="mb-1.5 mt-5 block text-sm font-semibold text-ink">
          Dein Name
        </label>
        <input
          id={nameId}
          value={name}
          maxLength={NAME_MAX_LENGTH}
          autoComplete="nickname"
          autoFocus
          aria-invalid={error !== null}
          aria-describedby={error ? `${nameId}-error` : undefined}
          onChange={(e) => {
            setName(e.target.value);
            setError(null);
          }}
          className={inputClass}
        />
        {error && (
          <p id={`${nameId}-error`} role="alert" className="mt-1.5 text-sm text-danger">
            {error}
          </p>
        )}
        <div className="mt-4 flex flex-wrap gap-2">
          <Button type="submit" variant="primary" className="min-h-12 px-6" disabled={busy}>
            {busy ? "Trete bei ..." : "Beitreten"}
          </Button>
          <Button variant="ghost" onClick={() => navigate("/")}>
            Zur Startseite
          </Button>
        </div>
      </form>
    </Centered>
  );
}
